import type { AnimationMeta, FrameEvent } from '../anim/schema';
import { AnimationPlayer, type Facing } from '../anim/player';
import { PlaceholderSpriteSheet, type SpriteSheet } from '../anim/spritesheet';
import {
  DEFAULT_META,
  type EffectSpriteId,
  type EffectSpriteMeta,
  type EnvironmentFloorBandKey,
  type EnvironmentRoomPlateKey,
  type EnvironmentSurroundPanelBandKey,
  type EnvironmentTileKey,
  type LoadedEffectSprites,
  type LoadedEnvironmentTileset,
  type LoadedPickupAssets,
  type PickupAssetKey,
  monsterAssetIdForFloor,
} from '../assets/registry';
import type { EquippedCosmetic, Position, RunState } from '../api/types';
import { appearanceAssetIdForCosmetics } from '../cosmetics/visuals';
import type { EntityAnimState, GameEvent } from '../game/gameEvents';
import type { EffectsController, FloatingTextView, SparkView } from './effects';
import type { GameFeelStage } from './gameFeel';
import { AnimationTimeline } from './timeline';

export interface LoadedSpriteAsset {
  id: string;
  meta: AnimationMeta;
  sheet: SpriteSheet;
  color: string;
  label: string;
}

export interface CanvasStageControls {
  sfxKeys: () => string[];
  playSfx: (key: string) => void;
  isMuted: () => boolean;
  setMuted: (muted: boolean) => void;
  getMasterVolume: () => number;
  setMasterVolume: (v: number) => void;
  getSfxVolume: () => number;
  setSfxVolume: (v: number) => void;
  triggerEvents: (events: GameEvent[]) => void;
}

export interface CanvasStageOptions {
  effects: EffectsController;
  onFrameEvents?: (events: FrameEvent[], context: { entityId: string; position: Position }) => void;
  now?: () => number;
}

interface StageEntity {
  id: string;
  assetId: string;
  position: Position;
  player: AnimationPlayer;
  facing: Facing;
  transientUntil: number | null;
  deathHoldUntil: number | null;
}

interface NamedEffect {
  name: string;
  position: Position;
  startedAt: number;
  durationMs: number;
}

interface PreviewState {
  entityId: string;
  stateName: string;
  paused: boolean;
  player: AnimationPlayer | null;
  timeline: AnimationTimeline;
  eventLog: Array<{ atMs: number; frame: number; type: string; name?: string }>;
}

const STAGE_TILE_MIN = 96;
const STAGE_TILE_MAX = 132;
const STAGE_PAD = 32;
const DEATH_HOLD_MS = 900;
const DEFAULT_EFFECT_DURATION_MS = 320;
const fallbackColors: Record<string, string> = {
  player: '#e8a13c',
  monster_001: '#c46a5a',
  monster_002: '#9a7464',
  monster_003: '#586070',
};
const fallbackLabels: Record<string, string> = {
  player: 'PLR',
  monster_001: 'MON',
  monster_002: 'OSR',
  monster_003: 'WRD',
};
const effectNameAliases: Record<string, EffectSpriteId> = {
  slash_arc: 'vfx_slash_gravelight_01',
  death_puff: 'vfx_death_puff_gravebound_01',
};

interface StageDimensions {
  width: number;
  height: number;
}

interface StageCanvasMetrics {
  tile: number;
  pad: number;
  padX: number;
  padY: number;
  cssWidth: number;
  cssHeight: number;
}

type QuarterTurn = 0 | 1 | 2 | 3;

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface EnvironmentTilePlacement {
  key: EnvironmentTileKey;
  rotationTurns: QuarterTurn;
}

export interface SurroundBackdropPlan {
  baseRect: Rect;
  roomRect: Rect;
  outsideBands: Rect[];
  textureRects: Rect[];
  panelRects: SurroundPanelRect[];
  masonryRects: Rect[];
  patchRects: SurroundPatchRect[];
}

interface SurroundPanelRect {
  panelIndex: number;
  rect: Rect;
}

interface SurroundPatchRect {
  asset: 'props' | 'ferryman';
  rect: Rect;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function monsterAssetIdForRun(run: RunState | null): string {
  return monsterAssetIdForFloor(run?.floorState.floor ?? run?.floor ?? 1);
}

function defaultAssetIdForEntity(run: RunState | null, entityId: string): string {
  return entityId === 'player' ? 'player' : monsterAssetIdForRun(run);
}

export function stageCanvasMetrics(
  stage: StageDimensions,
  constraints: { maxCssWidth?: number; maxCssHeight?: number } = {},
): StageCanvasMetrics {
  const gridWidth = Math.max(1, stage.width);
  const gridHeight = Math.max(1, stage.height);
  const maxByWidth = constraints.maxCssWidth
    ? Math.floor((constraints.maxCssWidth - STAGE_PAD * 2) / gridWidth)
    : STAGE_TILE_MAX;
  const maxByHeight = constraints.maxCssHeight
    ? Math.floor((constraints.maxCssHeight - STAGE_PAD * 2) / gridHeight)
    : STAGE_TILE_MAX;
  const tile = clamp(Math.min(maxByWidth, maxByHeight, STAGE_TILE_MAX), STAGE_TILE_MIN, STAGE_TILE_MAX);
  const roomWidth = gridWidth * tile;
  const roomHeight = gridHeight * tile;
  const minCssWidth = roomWidth + STAGE_PAD * 2;
  const minCssHeight = roomHeight + STAGE_PAD * 2;
  const cssWidth = constraints.maxCssWidth ? Math.max(minCssWidth, Math.floor(constraints.maxCssWidth)) : minCssWidth;
  const cssHeight = constraints.maxCssHeight ? Math.max(minCssHeight, Math.floor(constraints.maxCssHeight)) : minCssHeight;
  return {
    tile,
    pad: STAGE_PAD,
    padX: Math.max(STAGE_PAD, Math.floor((cssWidth - roomWidth) / 2)),
    padY: Math.max(STAGE_PAD, Math.floor((cssHeight - roomHeight) / 2)),
    cssWidth,
    cssHeight,
  };
}

export function environmentTileKey(pos: Position, stage: StageDimensions, floor: number): EnvironmentTileKey {
  const maxX = Math.max(0, stage.width - 1);
  const maxY = Math.max(0, stage.height - 1);
  const onHorizontalEdge = pos.y === 0 || pos.y === maxY;
  const onVerticalEdge = pos.x === 0 || pos.x === maxX;
  if (onHorizontalEdge && onVerticalEdge) return 'boundary';
  if (onHorizontalEdge || onVerticalEdge) return 'edge';

  const centerX = Math.floor(maxX / 2);
  const centerY = Math.floor(maxY / 2);
  const isCenter = pos.x === centerX && pos.y === centerY;
  const variation = (pos.x * 7 + pos.y * 11 + floor * 3) % 6;
  switch (catacombsFloorBand(floor)) {
    case 1:
      return !isCenter && variation <= 1 ? 'cracked' : 'floor';
    case 2:
      return isCenter || variation <= 2 ? 'cracked' : 'floor';
    case 3:
      return isCenter || variation <= 2 ? 'grave' : 'cracked';
  }
}

export function environmentTilePlacement(pos: Position, stage: StageDimensions, floor: number): EnvironmentTilePlacement {
  const key = environmentTileKey(pos, stage, floor);
  const rotationTurns = key === 'boundary' || key === 'edge' ? roomPerimeterRotation(pos, stage) : 0;
  return { key, rotationTurns };
}

export function environmentFloorBandKey(floor: number): EnvironmentFloorBandKey {
  return `f${catacombsFloorBand(floor)}` as EnvironmentFloorBandKey;
}

export function environmentRoomPlateKey(floor: number): EnvironmentRoomPlateKey {
  return environmentFloorBandKey(floor);
}

export function environmentSurroundPanelBandKey(floor: number): EnvironmentSurroundPanelBandKey {
  return `floor${catacombsFloorBand(floor)}` as EnvironmentSurroundPanelBandKey;
}

export function environmentRoomPlateForFloor(tileset: LoadedEnvironmentTileset | null, floor: number): HTMLImageElement | null {
  return tileset?.roomPlates?.[environmentRoomPlateKey(floor)] ?? null;
}

export function environmentSurroundTextureForFloor(tileset: LoadedEnvironmentTileset | null, floor: number): HTMLImageElement | null {
  return tileset?.surroundAssets?.textures[environmentFloorBandKey(floor)] ?? null;
}

export function environmentSurroundPanelsForFloor(tileset: LoadedEnvironmentTileset | null, floor: number): HTMLImageElement[] {
  return tileset?.surroundPanels?.[environmentSurroundPanelBandKey(floor)] ?? [];
}

function environmentMarkerProp(
  tileset: LoadedEnvironmentTileset | null,
  key: 'ferryStep' | 'descentStair',
): HTMLImageElement | null {
  return tileset?.markerProps?.[key] ?? null;
}

function itemPickupAssetKey(slotType: string | null | undefined): PickupAssetKey {
  void slotType;
  return 'itemChest';
}

export function surroundBackdropPlan(
  stageRect: StageDimensions,
  room: StageDimensions,
  metrics: StageCanvasMetrics,
  floor: number,
): SurroundBackdropPlan {
  const baseRect = { x: 0, y: 0, width: stageRect.width, height: stageRect.height };
  const roomRect = roomRectForStage(room, metrics);
  const outsideBands = outsideRoomBands(baseRect, roomRect);
  const textureSize = Math.max(48, Math.floor(metrics.tile * 0.5));
  const offsetX = -((floor * 17) % textureSize);
  const offsetY = -((floor * 29) % textureSize);
  const textureRects: Rect[] = [];
  for (let y = offsetY; y < stageRect.height; y += textureSize) {
    for (let x = offsetX; x < stageRect.width; x += textureSize) {
      textureRects.push({ x, y, width: textureSize, height: textureSize });
    }
  }

  return {
    baseRect,
    roomRect,
    outsideBands,
    textureRects,
    panelRects: surroundPanelRects(outsideBands, floor, metrics.tile),
    masonryRects: surroundMasonryRects(baseRect, roomRect, floor, metrics.tile),
    patchRects: surroundPatchRects(outsideBands, floor, metrics.tile),
  };
}

function roomRectForStage(stage: StageDimensions, metrics: StageCanvasMetrics): Rect {
  return {
    x: metrics.padX,
    y: metrics.padY,
    width: stage.width * metrics.tile - 2,
    height: stage.height * metrics.tile - 2,
  };
}

function outsideRoomBands(stage: Rect, room: Rect): Rect[] {
  const roomRight = room.x + room.width;
  const roomBottom = room.y + room.height;
  return [
    { x: stage.x, y: stage.y, width: stage.width, height: Math.max(0, room.y - stage.y) },
    { x: roomRight, y: room.y, width: Math.max(0, stage.x + stage.width - roomRight), height: room.height },
    { x: stage.x, y: roomBottom, width: stage.width, height: Math.max(0, stage.y + stage.height - roomBottom) },
    { x: stage.x, y: room.y, width: Math.max(0, room.x - stage.x), height: room.height },
  ].filter((rect) => rect.width > 0 && rect.height > 0);
}

function intersectRect(a: Rect, b: Rect): Rect | null {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  if (right <= x || bottom <= y) return null;
  return { x, y, width: right - x, height: bottom - y };
}

function roomPerimeterRotation(pos: Position, stage: StageDimensions): QuarterTurn {
  const maxX = Math.max(0, stage.width - 1);
  const maxY = Math.max(0, stage.height - 1);
  if (pos.x === 0 && pos.y === 0) return 0;
  if (pos.x === maxX && pos.y === 0) return 1;
  if (pos.x === maxX && pos.y === maxY) return 2;
  if (pos.x === 0 && pos.y === maxY) return 3;
  if (pos.y === 0) return 0;
  if (pos.x === maxX) return 1;
  if (pos.y === maxY) return 2;
  if (pos.x === 0) return 3;
  return 0;
}

function surroundMasonryRects(stage: Rect, room: Rect, floor: number, tile: number): Rect[] {
  const roomRight = room.x + room.width;
  const roomBottom = room.y + room.height;
  const bands = [
    { x: 0, y: 0, width: stage.width, height: room.y },
    { x: roomRight, y: room.y, width: stage.width - roomRight, height: room.height },
    { x: 0, y: roomBottom, width: stage.width, height: stage.height - roomBottom },
    { x: 0, y: room.y, width: room.x, height: room.height },
  ].filter((band) => band.width > 0 && band.height > 0);

  const rects: Rect[] = [];
  for (const [bandIndex, band] of bands.entries()) {
    const area = band.width * band.height;
    const count = Math.max(4, Math.min(24, Math.ceil(area / Math.max(480, tile * 9))));
    for (let i = 0; i < count; i += 1) {
      const width = Math.max(4, Math.min(band.width, 8 + ((i * 11 + floor * 5 + bandIndex * 7) % Math.max(9, Math.floor(tile * 0.2)))));
      const height = Math.max(2, Math.min(band.height, 2 + ((i * 7 + floor * 3 + bandIndex * 5) % Math.max(5, Math.floor(tile * 0.11)))));
      const xRange = Math.max(1, Math.floor(band.width - width));
      const yRange = Math.max(1, Math.floor(band.height - height));
      rects.push({
        x: Math.round(band.x + ((i * 47 + floor * 19 + bandIndex * 31) % xRange)),
        y: Math.round(band.y + ((i * 31 + floor * 23 + bandIndex * 17) % yRange)),
        width,
        height,
      });
    }
  }
  return rects;
}

function surroundPanelRects(bands: Rect[], floor: number, tile: number): SurroundPanelRect[] {
  const panelSize = clamp(Math.round(tile * 2.6), 240, 320);
  const stride = Math.max(144, Math.floor(panelSize * 0.78));
  const rects: SurroundPanelRect[] = [];

  for (const [bandIndex, band] of bands.entries()) {
    if (band.width < 12 || band.height < 12) continue;
    const horizontal = band.width >= band.height;
    const length = horizontal ? band.width : band.height;
    const count = Math.max(1, Math.min(5, Math.ceil(length / stride)));
    const segment = length / count;
    for (let i = 0; i < count; i += 1) {
      const offset = ((floor * 17 + bandIndex * 23 + i * 11) % 37) - 18;
      const centerAlong = segment * (i + 0.5) + offset;
      const centerX = horizontal ? band.x + clamp(centerAlong, 0, band.width) : band.x + band.width / 2;
      const centerY = horizontal ? band.y + band.height / 2 : band.y + clamp(centerAlong, 0, band.height);
      const x = bandIndex === 1
        ? band.x + band.width - panelSize
        : bandIndex === 3
          ? band.x
          : centerX - panelSize / 2;
      const y = bandIndex === 0
        ? band.y
        : bandIndex === 2
          ? band.y + band.height - panelSize
          : centerY - panelSize / 2;
      rects.push({
        panelIndex: (floor + bandIndex + i) % 2,
        rect: {
          x: Math.round(x),
          y: Math.round(y),
          width: panelSize,
          height: panelSize,
        },
      });
    }
  }

  return rects;
}

function surroundPatchRects(bands: Rect[], floor: number, tile: number): SurroundPatchRect[] {
  const rects: SurroundPatchRect[] = [];
  const baseSize = clamp(Math.round(tile * 1.45), 96, 184);
  for (const [bandIndex, band] of bands.entries()) {
    if (band.width < 16 || band.height < 16) continue;
    const width = Math.max(16, Math.min(band.width, baseSize));
    const height = Math.max(16, Math.min(band.height, baseSize));
    const xRange = Math.max(1, Math.floor(band.width - width));
    const yRange = Math.max(1, Math.floor(band.height - height));
    rects.push({
      asset: 'props',
      rect: {
        x: Math.round(band.x + ((floor * 37 + bandIndex * 71) % xRange)),
        y: Math.round(band.y + ((floor * 53 + bandIndex * 43) % yRange)),
        width,
        height,
      },
    });
  }

  if (catacombsFloorBand(floor) === 3 && bands.length > 0) {
    const firstBand = bands[0];
    if (!firstBand) return rects;
    const largest = bands.slice(1).reduce((best, band) => (band.width * band.height > best.width * best.height ? band : best), firstBand);
    if (largest.width >= 18 && largest.height >= 18) {
      const width = Math.max(18, Math.min(largest.width, Math.round(baseSize * 1.2)));
      const height = Math.max(18, Math.min(largest.height, Math.round(baseSize * 1.2)));
      const xRange = Math.max(1, Math.floor(largest.width - width));
      const yRange = Math.max(1, Math.floor(largest.height - height));
      rects.push({
        asset: 'ferryman',
        rect: {
          x: Math.round(largest.x + ((floor * 97 + 13) % xRange)),
          y: Math.round(largest.y + ((floor * 89 + 29) % yRange)),
          width,
          height,
        },
      });
    }
  }

  return rects;
}

function catacombsFloorBand(floor: number): 1 | 2 | 3 {
  if (floor <= 1) return 1;
  if (floor === 2) return 2;
  return 3;
}

function clonePosition(position: Position): Position {
  return { x: position.x, y: position.y };
}

function effectSpriteId(name: string): EffectSpriteId | null {
  if (name === 'vfx_slash_gravelight_01') return name;
  if (name === 'vfx_hit_impact_gravebone_01') return name;
  if (name === 'vfx_death_puff_gravebound_01') return name;
  if (name === 'vfx_pickup_sparkle_01') return name;
  if (name === 'vfx_extraction_seal_01') return name;
  return effectNameAliases[name] ?? null;
}

function effectDuration(meta: EffectSpriteMeta | null): number {
  if (!meta) return DEFAULT_EFFECT_DURATION_MS;
  return meta.durationMs ?? Math.ceil((meta.frames.length / meta.fps) * 1000);
}

function hasState(meta: AnimationMeta, state: string): boolean {
  return Object.prototype.hasOwnProperty.call(meta.states, state);
}

function entityPosition(run: RunState | null, id: string): Position {
  if (id === 'player') return run?.position ?? { x: 0, y: 0 };
  const enemyId = id.startsWith('enemy:') ? id.slice('enemy:'.length) : id;
  return run?.floorState.enemies.find((enemy) => enemy.id === enemyId && enemy.hp > 0)?.position ?? { x: 0, y: 0 };
}

function htmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function optionHtml(values: string[], selected: string): string {
  return values.map((value) => {
    const safe = htmlEscape(value);
    return '<option value="' + safe + '" ' + (value === selected ? 'selected' : '') + '>' + safe + '</option>';
  }).join('');
}

export class CanvasStage implements GameFeelStage {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private previewCanvas: HTMLCanvasElement | null = null;
  private previewCtx: CanvasRenderingContext2D | null = null;
  private readout: HTMLElement | null = null;
  private run: RunState | null = null;
  private readonly entities = new Map<string, StageEntity>();
  private readonly assets = new Map<string, LoadedSpriteAsset>();
  private readonly fallbackAssets = new Map<string, LoadedSpriteAsset>();
  private readonly timeline: AnimationTimeline;
  private rafId: number | null = null;
  private realNow: number;
  private animationNow = 0;
  private namedEffects: NamedEffect[] = [];
  private controls: CanvasStageControls | null = null;
  private metrics: StageCanvasMetrics = stageCanvasMetrics({ width: 5, height: 5 });
  private environmentTileset: LoadedEnvironmentTileset | null = null;
  private pickupAssets: LoadedPickupAssets = {};
  private effectSprites: LoadedEffectSprites = {};
  private equippedCosmetics: EquippedCosmetic[] = [];
  private resizeListenerAttached = false;
  private preview: PreviewState;

  constructor(private readonly options: CanvasStageOptions) {
    this.realNow = options.now?.() ?? (typeof performance !== 'undefined' ? performance.now() : Date.now());
    this.timeline = new AnimationTimeline(this.realNow);
    this.preview = {
      entityId: 'player',
      stateName: 'idle',
      paused: false,
      player: null,
      timeline: new AnimationTimeline(this.realNow),
      eventLog: [],
    };
  }

  setAssets(assets: LoadedSpriteAsset[]): void {
    this.assets.clear();
    for (const asset of assets) this.assets.set(asset.id, asset);
    this.preview.player = null;
    for (const entity of this.entities.values()) {
      const asset = this.getAsset(entity.assetId);
      entity.player = new AnimationPlayer(asset.meta, { now: this.animationNow, facing: entity.facing });
    }
  }

  setEnvironmentTileset(tileset: LoadedEnvironmentTileset | null): void {
    this.environmentTileset = tileset;
  }

  setPickupAssets(assets: LoadedPickupAssets | null): void {
    this.pickupAssets = assets ?? {};
  }

  setEffectSprites(assets: LoadedEffectSprites | null): void {
    this.effectSprites = assets ?? {};
  }

  setEquippedCosmetics(cosmetics: readonly EquippedCosmetic[] | null | undefined): void {
    this.equippedCosmetics = cosmetics ? [...cosmetics] : [];
  }

  mount(root: HTMLElement, devMode: boolean, controls: CanvasStageControls): void {
    this.controls = controls;
    const nextCanvas = root.querySelector<HTMLCanvasElement>('[data-gamefeel-stage]');
    if (nextCanvas !== this.canvas) {
      this.canvas = nextCanvas;
      this.ctx = nextCanvas?.getContext('2d') ?? null;
      if (this.ctx) this.ctx.imageSmoothingEnabled = false;
    }
    if (!this.resizeListenerAttached && typeof window !== 'undefined') {
      window.addEventListener('resize', () => this.resizeCanvas(), { passive: true });
      this.resizeListenerAttached = true;
    }
    this.bindPreviewPanel(root, devMode);
    this.resizeCanvas();
    this.start();
  }

  setRun(run: RunState | null): void {
    this.run = run;
    this.syncEntities();
    this.resizeCanvas();
  }

  playEntity(entityId: string, state: EntityAnimState | string, opts: { facing?: Facing; transientMs?: number } = {}): void {
    const entity = this.entities.get(entityId) ?? this.createEntity(entityId, defaultAssetIdForEntity(this.run, entityId), entityPosition(this.run, entityId));
    if (opts.facing) {
      entity.facing = opts.facing;
      entity.player.setFacing(opts.facing);
    }
    const asset = this.getAsset(entity.assetId);
    const stateName = hasState(asset.meta, state) ? state : asset.meta.defaultState;
    entity.player.play(stateName, this.animationNow, { force: true });
    entity.transientUntil = opts.transientMs ? this.realNow + opts.transientMs : null;
    entity.deathHoldUntil = stateName === 'death' ? this.realNow + DEATH_HOLD_MS : null;
  }

  ensureEntity(entityId: string, assetId: string, position: Position): void {
    const existing = this.entities.get(entityId);
    if (existing) {
      existing.position = clonePosition(position);
      return;
    }
    this.createEntity(entityId, assetId, position);
  }

  spawnEffect(name: string, position: Position): void {
    const sprite = effectSpriteId(name);
    const durationMs = effectDuration(sprite ? (this.effectSprites[sprite]?.meta ?? null) : null);
    this.namedEffects.push({ name, position: clonePosition(position), startedAt: this.realNow, durationMs });
  }

  clearTransient(): void {
    this.namedEffects = [];
    const remove: string[] = [];
    for (const [id, entity] of this.entities) {
      entity.transientUntil = null;
      entity.deathHoldUntil = null;
      const asset = this.getAsset(entity.assetId);
      if (hasState(asset.meta, 'idle')) entity.player.play('idle', this.animationNow, { force: true });
      if (id !== 'player' && !this.run?.floorState.enemies.some((enemy) => 'enemy:' + enemy.id === id && enemy.hp > 0)) {
        remove.push(id);
      }
    }
    for (const id of remove) this.entities.delete(id);
  }

  triggerPreview(entityId: string, stateName: string, force = false): void {
    this.preview.entityId = entityId;
    this.preview.stateName = stateName;
    const asset = this.getAsset(entityId);
    if (!this.preview.player || force) {
      this.preview.player = new AnimationPlayer(asset.meta, { now: 0 });
      this.preview.timeline.reset(this.realNow);
      this.preview.eventLog = [];
    }
    const playable = hasState(asset.meta, stateName) ? stateName : asset.meta.defaultState;
    this.preview.player.play(playable, this.preview.timeline.now(), { force: true });
  }

  setPreviewPaused(paused: boolean): void {
    this.preview.paused = paused;
  }

  restartPreview(): void {
    this.preview.player = null;
    this.triggerPreview(this.preview.entityId, this.preview.stateName, true);
  }

  private start(): void {
    if (this.rafId !== null || typeof requestAnimationFrame === 'undefined') return;
    const tick = (ts: number) => {
      this.rafId = requestAnimationFrame(tick);
      this.draw(ts);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private syncEntities(): void {
    const run = this.run;
    if (!run) {
      this.entities.clear();
      return;
    }

    this.upsertEntity('player', 'player', run.position);
    const alive = new Set<string>(['player']);
    const monsterAssetId = monsterAssetIdForRun(run);
    for (const enemy of run.floorState.enemies.filter((entry) => entry.hp > 0)) {
      const id = 'enemy:' + enemy.id;
      alive.add(id);
      const entity = this.upsertEntity(id, monsterAssetId, enemy.position);
      if (entity.player.getStateName() === 'death') continue;
      entity.position = clonePosition(enemy.position);
    }

    for (const [id, entity] of this.entities) {
      if (!alive.has(id) && entity.player.getStateName() !== 'death') this.entities.delete(id);
    }
  }

  private upsertEntity(id: string, assetId: string, position: Position): StageEntity {
    const existing = this.entities.get(id);
    if (existing) {
      existing.position = clonePosition(position);
      if (existing.assetId !== assetId) {
        existing.assetId = assetId;
        const asset = this.getAsset(assetId);
        existing.player = new AnimationPlayer(asset.meta, { now: this.animationNow, facing: existing.facing });
      }
      return existing;
    }
    return this.createEntity(id, assetId, position);
  }

  private createEntity(id: string, assetId: string, position: Position): StageEntity {
    const asset = this.getAsset(assetId);
    const entity: StageEntity = {
      id,
      assetId,
      position: clonePosition(position),
      player: new AnimationPlayer(asset.meta, { now: this.animationNow }),
      facing: 'right',
      transientUntil: null,
      deathHoldUntil: null,
    };
    this.entities.set(id, entity);
    return entity;
  }

  private getAsset(id: string): LoadedSpriteAsset {
    const direct = this.assets.get(id);
    if (direct) return direct;
    const fallbackId = id === 'player' || id.startsWith('monster_') ? id : 'monster_001';
    let fallback = this.fallbackAssets.get(fallbackId);
    if (!fallback) {
      const color = fallbackColors[fallbackId] ?? '#8a8175';
      const label = fallbackLabels[fallbackId] ?? '???';
      fallback = {
        id: fallbackId,
        meta: DEFAULT_META,
        sheet: new PlaceholderSpriteSheet(DEFAULT_META.frameWidth, DEFAULT_META.frameHeight, color, label, 21),
        color,
        label,
      };
      this.fallbackAssets.set(fallbackId, fallback);
    }
    return fallback;
  }

  private displayAssetForEntity(entity: StageEntity): LoadedSpriteAsset {
    if (entity.id === 'player') {
      const skinAssetId = appearanceAssetIdForCosmetics(this.equippedCosmetics);
      if (skinAssetId) {
        const skinAsset = this.assets.get(skinAssetId);
        if (skinAsset) return skinAsset;
      }
    }
    return this.getAsset(entity.assetId);
  }

  private draw(realNow: number): void {
    this.realNow = realNow;
    this.animationNow = this.timeline.tick(realNow, this.options.effects.isHitStopped(realNow));
    this.options.effects.update(realNow);
    this.namedEffects = this.namedEffects.filter((effect) => realNow - effect.startedAt < effect.durationMs);
    this.drawMain(realNow);
    this.drawPreview(realNow);
    this.updateReadout();
  }

  private resizeCanvas(): void {
    if (!this.canvas || !this.run) return;
    const wrap = this.canvas.parentElement;
    const maxCssWidth = wrap ? Math.max(0, wrap.clientWidth - 16) : undefined;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : undefined;
    const maxCssHeight = viewportHeight ? Math.min(780, Math.max(560, viewportHeight - 118)) : undefined;
    const metrics = stageCanvasMetrics(this.run.floorState, { maxCssWidth, maxCssHeight });
    this.metrics = metrics;
    const { cssWidth, cssHeight } = metrics;
    const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
    const pxWidth = cssWidth * dpr;
    const pxHeight = cssHeight * dpr;
    this.canvas.style.width = String(cssWidth) + 'px';
    this.canvas.style.height = String(cssHeight) + 'px';
    if (this.canvas.width !== pxWidth || this.canvas.height !== pxHeight) {
      this.canvas.width = pxWidth;
      this.canvas.height = pxHeight;
    }
  }

  private withCanvasScale(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, draw: () => void): void {
    const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    draw();
    ctx.restore();
  }

  private drawMain(realNow: number): void {
    const { canvas, ctx, run } = this;
    if (!canvas || !ctx || !run) return;
    this.withCanvasScale(ctx, canvas, () => {
      const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      this.drawSurround(ctx, width, height, run);
      const shake = this.options.effects.getShake(realNow);
      ctx.save();
      ctx.translate(Math.round(shake.x), Math.round(shake.y));
      this.drawTiles(ctx, run);
      this.drawMarkers(ctx, run);
      this.drawEntities(ctx, realNow);
      this.drawNamedEffects(ctx, realNow);
      this.drawSparks(ctx, this.options.effects.getSparks(realNow));
      this.drawPulse(ctx, run, realNow);
      this.drawFloatingTexts(ctx, this.options.effects.getFloatingTexts(realNow));
      ctx.restore();
      if (this.options.effects.isHitStopped(realNow)) {
        ctx.fillStyle = '#ffffff18';
        ctx.fillRect(0, 0, width, height);
      }
    });
  }

  private drawSurround(ctx: CanvasRenderingContext2D, width: number, height: number, run: RunState): void {
    const band = catacombsFloorBand(run.floor);
    const palette = band === 1
      ? { base: '#0d0a08', dust: '#332820', shadow: '#00000055', block: '#1b1714' }
      : band === 2
        ? { base: '#0a0908', dust: '#2b2924', shadow: '#00000066', block: '#181716' }
        : { base: '#080708', dust: '#2d241c', shadow: '#00000078', block: '#171312' };
    const plan = surroundBackdropPlan({ width, height }, run.floorState, this.metrics, run.floor);
    const surroundPanels = environmentSurroundPanelsForFloor(this.environmentTileset, run.floor);
    const hasScenicPanels = surroundPanels.length > 0;
    ctx.fillStyle = palette.base;
    ctx.fillRect(plan.baseRect.x, plan.baseRect.y, plan.baseRect.width, plan.baseRect.height);

    const richSurround = environmentSurroundTextureForFloor(this.environmentTileset, run.floor);
    const surround = richSurround ?? this.environmentTileset?.tiles.surround;
    if (surround) {
      ctx.save();
      ctx.globalAlpha = richSurround ? (band === 3 ? 0.88 : band === 2 ? 0.84 : 0.82) : band === 3 ? 0.78 : band === 2 ? 0.68 : 0.6;
      ctx.imageSmoothingEnabled = false;
      for (const rect of plan.textureRects) {
        this.drawImageInsideBands(ctx, surround, rect, plan.outsideBands);
      }
      ctx.restore();
    }

    ctx.save();
    ctx.globalAlpha = band === 3 ? 0.34 : 0.27;
    ctx.fillStyle = palette.block;
    for (const rect of plan.masonryRects) {
      ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    }
    ctx.globalAlpha = band === 3 ? 0.46 : 0.34;
    ctx.fillStyle = palette.dust;
    for (const [i, rect] of plan.masonryRects.entries()) {
      if (i % 2 !== 0) continue;
      ctx.fillRect(rect.x + Math.floor(rect.width / 3), rect.y, Math.max(2, Math.floor(rect.width / 2)), 1);
    }
    ctx.restore();

    if (hasScenicPanels) {
      ctx.save();
      ctx.globalAlpha = band === 3 ? 0.82 : band === 2 ? 0.78 : 0.72;
      ctx.imageSmoothingEnabled = false;
      if (this.clipToBands(ctx, plan.outsideBands)) {
        for (const panel of plan.panelRects) {
          const image = surroundPanels[panel.panelIndex % surroundPanels.length];
          if (!image) continue;
          this.drawImageInsideBands(ctx, image, panel.rect, plan.outsideBands);
        }
      }
      ctx.restore();
    }

    if (!hasScenicPanels && richSurround && this.environmentTileset?.surroundAssets) {
      ctx.save();
      ctx.globalAlpha = band === 3 ? 0.72 : band === 2 ? 0.64 : 0.58;
      ctx.imageSmoothingEnabled = false;
      for (const patch of plan.patchRects) {
        const image = patch.asset === 'ferryman'
          ? this.environmentTileset.surroundAssets.ferryman
          : this.environmentTileset.surroundAssets.props;
        if (!image) continue;
        this.drawImageInsideBands(ctx, image, patch.rect, plan.outsideBands);
      }
      ctx.restore();
    }

    const { roomRect } = plan;
    const fade = Math.max(10, Math.floor(this.metrics.tile * 0.12));
    ctx.save();
    ctx.fillStyle = palette.shadow;
    ctx.fillRect(roomRect.x - fade, roomRect.y - fade, roomRect.width + fade * 2, fade);
    ctx.fillRect(roomRect.x - fade, roomRect.y + roomRect.height, roomRect.width + fade * 2, fade);
    ctx.fillRect(roomRect.x - fade, roomRect.y, fade, roomRect.height);
    ctx.fillRect(roomRect.x + roomRect.width, roomRect.y, fade, roomRect.height);
    ctx.restore();

    const vignette = ctx.createRadialGradient(width / 2, height / 2, Math.min(width, height) * 0.24, width / 2, height / 2, Math.max(width, height) * 0.68);
    vignette.addColorStop(0, '#00000000');
    vignette.addColorStop(1, band === 3 ? '#00000074' : '#00000058');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, width, height);
  }

  private clipToBands(ctx: CanvasRenderingContext2D, bands: Rect[]): boolean {
    if (bands.length === 0) return false;
    ctx.beginPath();
    for (const band of bands) {
      ctx.rect(band.x, band.y, band.width, band.height);
    }
    ctx.clip();
    return true;
  }

  private drawImageInsideBands(ctx: CanvasRenderingContext2D, image: HTMLImageElement, target: Rect, bands: Rect[]): void {
    if (target.width <= 0 || target.height <= 0) return;
    for (const band of bands) {
      const visible = intersectRect(target, band);
      if (!visible) continue;
      const sx = ((visible.x - target.x) / target.width) * image.width;
      const sy = ((visible.y - target.y) / target.height) * image.height;
      const sw = (visible.width / target.width) * image.width;
      const sh = (visible.height / target.height) * image.height;
      ctx.drawImage(image, sx, sy, sw, sh, visible.x, visible.y, visible.width, visible.height);
    }
  }

  private drawTiles(ctx: CanvasRenderingContext2D, run: RunState): void {
    const { tile, padX, padY } = this.metrics;
    const roomPlate = environmentRoomPlateForFloor(this.environmentTileset, run.floor);
    if (roomPlate) {
      this.drawRoomPlate(ctx, roomPlate, run);
      this.drawGrid(ctx, run, '#2d241d');
      return;
    }

    for (let y = 0; y < run.floorState.height; y += 1) {
      for (let x = 0; x < run.floorState.width; x += 1) {
        const px = padX + x * tile;
        const py = padY + y * tile;
        const placement = environmentTilePlacement({ x, y }, run.floorState, run.floor);
        const tileImage = this.environmentTileset?.tiles[placement.key] ?? this.environmentTileset?.tiles.floor;
        if (tileImage) {
          this.drawEnvironmentTile(ctx, tileImage, px, py, tile - 2, tile - 2, placement.rotationTurns);
        } else {
          ctx.fillStyle = (x + y) % 2 === 0 ? '#16120e' : '#13100d';
          ctx.fillRect(px, py, tile - 2, tile - 2);
        }
        ctx.strokeStyle = tileImage ? '#2d241d' : '#241e18';
        ctx.strokeRect(px + 0.5, py + 0.5, tile - 3, tile - 3);
      }
    }
    this.drawRoomFrame(ctx, run);
  }

  private drawRoomPlate(ctx: CanvasRenderingContext2D, image: HTMLImageElement, run: RunState): void {
    const { tile, padX, padY } = this.metrics;
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(
      image,
      0,
      0,
      image.width,
      image.height,
      padX,
      padY,
      run.floorState.width * tile - 2,
      run.floorState.height * tile - 2,
    );
    ctx.restore();
  }

  private drawGrid(ctx: CanvasRenderingContext2D, run: RunState, strokeStyle: string): void {
    const { tile, padX, padY } = this.metrics;
    ctx.save();
    ctx.strokeStyle = strokeStyle;
    for (let y = 0; y < run.floorState.height; y += 1) {
      for (let x = 0; x < run.floorState.width; x += 1) {
        const px = padX + x * tile;
        const py = padY + y * tile;
        ctx.strokeRect(px + 0.5, py + 0.5, tile - 3, tile - 3);
      }
    }
    ctx.restore();
  }

  private drawEnvironmentTile(
    ctx: CanvasRenderingContext2D,
    image: HTMLImageElement,
    x: number,
    y: number,
    width: number,
    height: number,
    rotationTurns: QuarterTurn,
  ): void {
    if (rotationTurns === 0) {
      ctx.drawImage(image, 0, 0, image.width, image.height, x, y, width, height);
      return;
    }
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    if (rotationTurns === 1) {
      ctx.translate(x + width, y);
      ctx.rotate(Math.PI / 2);
    } else if (rotationTurns === 2) {
      ctx.translate(x + width, y + height);
      ctx.rotate(Math.PI);
    } else {
      ctx.translate(x, y + height);
      ctx.rotate((Math.PI * 3) / 2);
    }
    ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, width, height);
    ctx.restore();
  }

  private drawRoomFrame(ctx: CanvasRenderingContext2D, run: RunState): void {
    const { tile, padX, padY } = this.metrics;
    const width = run.floorState.width * tile - 2;
    const height = run.floorState.height * tile - 2;
    const band = catacombsFloorBand(run.floor);
    const shadowAlpha = band === 3 ? '5c' : band === 2 ? '4c' : '3d';
    const stroke = band === 3 ? '#4a3426' : band === 2 ? '#40352c' : '#493a2d';
    ctx.save();
    ctx.fillStyle = '#000000' + shadowAlpha;
    const fade = Math.max(10, Math.floor(tile * 0.12));
    ctx.fillRect(padX, padY, width, fade);
    ctx.fillRect(padX, padY + height - fade, width, fade);
    ctx.fillRect(padX, padY, fade, height);
    ctx.fillRect(padX + width - fade, padY, fade, height);
    ctx.strokeStyle = '#080605';
    ctx.lineWidth = 5;
    ctx.strokeRect(padX - 3.5, padY - 3.5, width + 5, height + 5);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 2;
    ctx.strokeRect(padX - 0.5, padY - 0.5, width - 1, height - 1);
    ctx.restore();
  }

  private drawMarkers(ctx: CanvasRenderingContext2D, run: RunState): void {
    const drawGlyph = (pos: Position, glyph: string, color: string) => {
      const center = this.tileCenter(pos);
      ctx.fillStyle = color;
      ctx.font = '20px ui-monospace, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(glyph, center.x, center.y);
    };
    const drawPropOrGlyph = (pos: Position, image: HTMLImageElement | null, glyph: string, color: string) => {
      if (image && this.drawMarkerProp(ctx, pos, image)) return;
      drawGlyph(pos, glyph, color);
    };
    const drawPickupOrGlyph = (pos: Position, image: HTMLImageElement | undefined, glyph: string, color: string) => {
      if (image && this.drawPickupProp(ctx, pos, image)) return;
      drawGlyph(pos, glyph, color);
    };
    if (run.floorState.ferryStep) {
      drawPropOrGlyph(run.floorState.ferryStep, environmentMarkerProp(this.environmentTileset, 'ferryStep'), 'F', '#5a9bd8');
    }
    drawPropOrGlyph(run.floorState.descentStair, environmentMarkerProp(this.environmentTileset, 'descentStair'), '▼', '#b07ad8');
    for (const gold of run.floorState.goldCaches) {
      if (!gold.collected) drawPickupOrGlyph(gold.position, this.pickupAssets.goldCache, '$', '#d8b24a');
    }
    for (const drop of run.floorState.itemDrops) {
      if (drop.item.state === 'found') {
        drawPickupOrGlyph(drop.position, this.pickupAssets[itemPickupAssetKey(drop.item.slotType)], '!', '#6fae6f');
      }
    }
  }

  private drawMarkerProp(ctx: CanvasRenderingContext2D, pos: Position, image: HTMLImageElement): boolean {
    const center = this.tileCenter(pos);
    const size = clamp(Math.round(this.metrics.tile * 0.78), 72, Math.max(72, this.metrics.tile - 12));
    const dx = Math.round(center.x - size / 2);
    const dy = Math.round(center.y - size / 2);
    try {
      ctx.save();
      ctx.globalAlpha = 0.96;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(image, 0, 0, image.width, image.height, dx, dy, size, size);
      ctx.restore();
      return true;
    } catch {
      ctx.restore();
      return false;
    }
  }

  private drawPickupProp(ctx: CanvasRenderingContext2D, pos: Position, image: HTMLImageElement): boolean {
    const center = this.tileCenter(pos);
    const size = clamp(Math.round(this.metrics.tile * 0.48), 40, 56);
    const dx = Math.round(center.x - size / 2);
    const dy = Math.round(center.y - size / 2);
    try {
      ctx.save();
      ctx.globalAlpha = 0.98;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(image, 0, 0, image.width, image.height, dx, dy, size, size);
      ctx.restore();
      return true;
    } catch {
      ctx.restore();
      return false;
    }
  }

  private drawEntities(ctx: CanvasRenderingContext2D, realNow: number): void {
    const sorted = [...this.entities.values()].sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);
    for (const entity of sorted) {
      this.maybeReturnToIdle(entity, realNow);
      const events = entity.player.update(this.animationNow);
      if (events.length > 0) this.options.onFrameEvents?.(events, { entityId: entity.id, position: entity.position });
      const asset = this.displayAssetForEntity(entity);
      const frame = entity.player.getCurrentFrame();
      const scale = (asset.meta.scale ?? 2) * (asset.meta.visualScale ?? 1);
      const sizeW = asset.sheet.frameWidth * scale;
      const sizeH = asset.sheet.frameHeight * scale;
      const center = this.tileCenter(entity.position);
      const tile = this.metrics.tile;
      const knock = this.options.effects.getKnockback(entity.id, realNow);
      const visualOffsetX = asset.meta.visualOffsetX ?? 0;
      const visualOffsetY = asset.meta.visualOffsetY ?? 0;
      const dx = Math.round(center.x - sizeW / 2 + visualOffsetX + knock.x);
      const dy = Math.round(center.y + tile * 0.36 - sizeH + visualOffsetY + knock.y);
      this.drawShadow(
        ctx,
        center.x + visualOffsetX,
        center.y + tile * 0.34 + (asset.meta.shadowOffsetY ?? 0),
        sizeW * 0.55 * (asset.meta.shadowScale ?? 1),
      );
      asset.sheet.draw(ctx, frame, dx, dy, scale, {
        flipX: entity.facing === 'left',
        flashAlpha: this.options.effects.getFlash(entity.id, realNow) * 0.7,
      });
    }
  }

  private maybeReturnToIdle(entity: StageEntity, realNow: number): void {
    const state = entity.player.getStateName();
    if (entity.transientUntil !== null && realNow >= entity.transientUntil && hasState(this.getAsset(entity.assetId).meta, 'idle')) {
      entity.player.play('idle', this.animationNow, { force: true });
      entity.transientUntil = null;
      return;
    }
    if (state !== 'death' && entity.player.isFinished() && hasState(this.getAsset(entity.assetId).meta, 'idle')) {
      entity.player.play('idle', this.animationNow, { force: true });
    }
    if (state === 'death' && entity.id !== 'player' && entity.deathHoldUntil !== null && realNow >= entity.deathHoldUntil) {
      this.entities.delete(entity.id);
    }
  }

  private drawShadow(ctx: CanvasRenderingContext2D, x: number, y: number, width: number): void {
    ctx.save();
    ctx.fillStyle = '#00000055';
    ctx.beginPath();
    ctx.ellipse(x, y, width / 2, 6, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawNamedEffects(ctx: CanvasRenderingContext2D, realNow: number): void {
    for (const effect of this.namedEffects) {
      const p = (realNow - effect.startedAt) / effect.durationMs;
      const center = this.tileCenter(effect.position);
      if (this.drawBitmapEffect(ctx, effect.name, center, p)) continue;
      this.drawCodeDrivenEffect(ctx, effect.name, center, p);
    }
  }

  private drawBitmapEffect(ctx: CanvasRenderingContext2D, name: string, center: Position, progress: number): boolean {
    const id = effectSpriteId(name);
    const sprite = id ? this.effectSprites[id] : undefined;
    if (!sprite) return false;
    const { meta, image } = sprite;
    const frameStep = clamp(Math.floor(progress * meta.frames.length), 0, meta.frames.length - 1);
    const frame = meta.frames[frameStep] ?? 0;
    const columns = Math.max(1, Math.floor(image.width / meta.frameWidth));
    const sx = (frame % columns) * meta.frameWidth;
    const sy = Math.floor(frame / columns) * meta.frameHeight;
    const scale = meta.scale ?? 1;
    const dw = meta.frameWidth * scale;
    const dh = meta.frameHeight * scale;
    const dx = Math.round(center.x - dw / 2);
    const dy = Math.round(center.y - dh / 2);
    try {
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, 1 - Math.max(0, progress - 0.72) / 0.28));
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(image, sx, sy, meta.frameWidth, meta.frameHeight, dx, dy, dw, dh);
      ctx.restore();
      return true;
    } catch {
      ctx.restore();
      return false;
    }
  }

  private drawCodeDrivenEffect(ctx: CanvasRenderingContext2D, name: string, center: Position, progress: number): void {
    const isSlash = name === 'slash_arc' || name === 'vfx_slash_gravelight_01';
    const isPickup = name === 'vfx_pickup_sparkle_01';
    const isSeal = name === 'vfx_extraction_seal_01';
    ctx.save();
    ctx.globalAlpha = 1 - progress;
    if (isPickup) {
      ctx.fillStyle = '#e8a13c';
      const r = 4 + progress * 10;
      ctx.fillRect(center.x - r, center.y - 1, r * 2, 2);
      ctx.fillRect(center.x - 1, center.y - r, 2, r * 2);
    } else {
      ctx.strokeStyle = isSlash ? '#e8e0d2' : isSeal ? '#d8b24a' : '#c46a5a';
      ctx.lineWidth = 3;
      ctx.beginPath();
      if (isSlash) {
        ctx.arc(center.x, center.y, 10 + progress * 20, -0.9, 0.9);
      } else {
        ctx.arc(center.x, center.y, 8 + progress * 26, 0, Math.PI * 2);
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawSparks(ctx: CanvasRenderingContext2D, sparks: SparkView[]): void {
    for (const spark of sparks) {
      const center = this.tileCenter({ x: spark.x, y: spark.y });
      ctx.save();
      ctx.globalAlpha = 1 - spark.progress;
      ctx.fillStyle = '#e8a13c';
      const r = 3 + spark.progress * 9;
      ctx.fillRect(center.x - r, center.y - 1, r * 2, 2);
      ctx.fillRect(center.x - 1, center.y - r, 2, r * 2);
      ctx.restore();
    }
  }

  private drawPulse(ctx: CanvasRenderingContext2D, run: RunState, realNow: number): void {
    const pulse = this.options.effects.getPulse(realNow);
    if (!pulse) return;
    const pos = pulse.kind === 'extract' ? run.floorState.ferryStep : run.floorState.descentStair;
    if (!pos) return;
    const center = this.tileCenter(pos);
    ctx.save();
    ctx.globalAlpha = 1 - pulse.progress;
    ctx.strokeStyle = pulse.kind === 'extract' ? '#5a9bd8' : '#b07ad8';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(center.x, center.y, 10 + pulse.progress * 34, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private drawFloatingTexts(ctx: CanvasRenderingContext2D, texts: FloatingTextView[]): void {
    for (const item of texts) {
      const center = this.tileCenter({ x: item.x, y: item.y });
      ctx.save();
      ctx.globalAlpha = 1 - item.progress;
      ctx.fillStyle = item.color;
      ctx.font = '700 14px ui-monospace, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(item.text, center.x, center.y - 24 - item.progress * 22);
      ctx.restore();
    }
  }

  private tileCenter(pos: Position): Position {
    const { tile, padX, padY } = this.metrics;
    return { x: padX + pos.x * tile + tile / 2, y: padY + pos.y * tile + tile / 2 };
  }

  private bindPreviewPanel(root: HTMLElement, devMode: boolean): void {
    const panel = root.querySelector<HTMLElement>('[data-gamefeel-panel]');
    if (!devMode || !panel || !this.controls) {
      this.previewCanvas = null;
      this.previewCtx = null;
      this.readout = null;
      return;
    }
    this.previewCanvas = panel.querySelector<HTMLCanvasElement>('[data-gf-preview-canvas]');
    this.previewCtx = this.previewCanvas?.getContext('2d') ?? null;
    this.readout = panel.querySelector<HTMLElement>('[data-gf-readout]');
    const entitySelect = panel.querySelector<HTMLSelectElement>('[data-gf-preview-entity]');
    const stateSelect = panel.querySelector<HTMLSelectElement>('[data-gf-preview-state]');
    const sfxSelect = panel.querySelector<HTMLSelectElement>('[data-gf-sfx]');
    const muted = panel.querySelector<HTMLInputElement>('[data-gf-muted]');
    const master = panel.querySelector<HTMLInputElement>('[data-gf-master-volume]');
    const sfxVolume = panel.querySelector<HTMLInputElement>('[data-gf-sfx-volume]');

    if (entitySelect) {
      entitySelect.innerHTML = optionHtml(this.availableEntityIds(), this.preview.entityId);
      entitySelect.onchange = () => {
        this.preview.entityId = entitySelect.value;
        this.preview.stateName = this.availableStates(entitySelect.value)[0] ?? 'idle';
        this.restartPreview();
      };
    }
    if (stateSelect) {
      stateSelect.innerHTML = optionHtml(this.availableStates(this.preview.entityId), this.preview.stateName);
      stateSelect.onchange = () => {
        this.preview.stateName = stateSelect.value;
        this.restartPreview();
      };
    }
    if (sfxSelect) sfxSelect.innerHTML = optionHtml(this.controls.sfxKeys(), sfxSelect.value);
    if (muted) {
      muted.checked = this.controls.isMuted();
      muted.onchange = () => this.controls?.setMuted(muted.checked);
    }
    if (master) {
      master.value = String(this.controls.getMasterVolume());
      master.oninput = () => this.controls?.setMasterVolume(Number(master.value));
    }
    if (sfxVolume) {
      sfxVolume.value = String(this.controls.getSfxVolume());
      sfxVolume.oninput = () => this.controls?.setSfxVolume(Number(sfxVolume.value));
    }

    for (const button of panel.querySelectorAll<HTMLButtonElement>('[data-gf-action]')) {
      button.onclick = () => this.handlePanelAction(button.dataset.gfAction ?? '', panel);
    }
  }

  private handlePanelAction(action: string, panel: HTMLElement): void {
    switch (action) {
      case 'preview-play':
        this.setPreviewPaused(false);
        return;
      case 'preview-pause':
        this.setPreviewPaused(true);
        return;
      case 'preview-restart':
        this.restartPreview();
        return;
      case 'sfx-play': {
        const key = panel.querySelector<HTMLSelectElement>('[data-gf-sfx]')?.value;
        if (key) this.controls?.playSfx(key);
        return;
      }
      case 'flash':
        this.controls?.triggerEvents([{ kind: 'flash', targetType: 'player' }]);
        return;
      case 'knockback':
        this.controls?.triggerEvents([{ kind: 'damage', targetType: 'player', amount: 1, pos: this.run?.position ?? { x: 0, y: 0 } }]);
        return;
      case 'shake':
        this.controls?.triggerEvents([{ kind: 'shake', intensity: 7 }]);
        return;
      case 'text':
        this.controls?.triggerEvents([{ kind: 'damage', targetType: 'player', amount: 7, pos: this.run?.position ?? { x: 0, y: 0 } }]);
        return;
      case 'sparkle':
        this.controls?.triggerEvents([{ kind: 'sparkle', pos: this.run?.position ?? { x: 0, y: 0 } }]);
        return;
      case 'vfx-slash':
        this.controls?.triggerEvents([{ kind: 'effect', name: 'vfx_slash_gravelight_01', pos: this.run?.position ?? { x: 0, y: 0 } }]);
        return;
      case 'vfx-hit-impact':
        this.controls?.triggerEvents([{ kind: 'effect', name: 'vfx_hit_impact_gravebone_01', pos: this.previewTargetPosition() }]);
        return;
      case 'vfx-death-puff':
        this.controls?.triggerEvents([{ kind: 'effect', name: 'vfx_death_puff_gravebound_01', pos: this.previewTargetPosition() }]);
        return;
      case 'vfx-pickup-sparkle':
        this.controls?.triggerEvents([
          { kind: 'sparkle', pos: this.run?.position ?? { x: 0, y: 0 } },
          { kind: 'effect', name: 'vfx_pickup_sparkle_01', pos: this.run?.position ?? { x: 0, y: 0 } },
        ]);
        return;
      case 'vfx-extraction-seal':
        this.controls?.triggerEvents([
          { kind: 'pulse', name: 'extract' },
          { kind: 'effect', name: 'vfx_extraction_seal_01', pos: this.run?.floorState.ferryStep ?? { x: 0, y: 0 } },
        ]);
        return;
      case 'pulse-extract':
        this.controls?.triggerEvents([{ kind: 'pulse', name: 'extract' }]);
        return;
      case 'pulse-descend':
        this.controls?.triggerEvents([{ kind: 'pulse', name: 'descend' }]);
        return;
    }
  }

  private availableEntityIds(): string[] {
    const ids = [...new Set(['player', 'monster_001', ...this.assets.keys()])];
    return ids.sort((a, b) => (a === 'player' ? -1 : b === 'player' ? 1 : a.localeCompare(b)));
  }

  private previewTargetPosition(): Position {
    const enemy = this.run?.floorState.enemies.find((entry) => entry.hp > 0);
    return enemy?.position ?? this.run?.position ?? { x: 0, y: 0 };
  }

  private availableStates(entityId: string): string[] {
    return Object.keys(this.getAsset(entityId).meta.states);
  }

  private drawPreview(realNow: number): void {
    const canvas = this.previewCanvas;
    const ctx = this.previewCtx;
    if (!canvas || !ctx) return;
    const asset = this.getAsset(this.preview.entityId);
    if (!this.preview.player) this.triggerPreview(this.preview.entityId, this.preview.stateName, true);
    const player = this.preview.player;
    if (!player) return;
    const previewNow = this.preview.timeline.tick(realNow, this.preview.paused);
    const events = player.update(previewNow);
    if (events.length > 0) {
      for (const event of events) {
        this.preview.eventLog.unshift({
          atMs: Math.round(previewNow),
          frame: event.frame,
          type: event.type,
          ...(event.name ? { name: event.name } : {}),
        });
      }
      this.preview.eventLog = this.preview.eventLog.slice(0, 8);
    }
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#0c0a08';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const scale = asset.meta.scale ?? 2;
    const dx = Math.round(canvas.width / 2 - (asset.sheet.frameWidth * scale) / 2);
    const dy = Math.round(canvas.height / 2 - (asset.sheet.frameHeight * scale) / 2);
    asset.sheet.draw(ctx, player.getCurrentFrame(), dx, dy, scale);
    ctx.strokeStyle = '#e8a13c80';
    ctx.strokeRect(dx + 0.5, dy + 0.5, asset.sheet.frameWidth * scale - 1, asset.sheet.frameHeight * scale - 1);
    ctx.fillStyle = '#e8a13c';
    ctx.fillRect(canvas.width / 2 - 2, canvas.height / 2 - 2, 4, 4);
  }

  private updateReadout(): void {
    if (!this.readout) return;
    const asset = this.getAsset(this.preview.entityId);
    const player = this.preview.player;
    const frame = player?.getCurrentFrame() ?? 0;
    const state = player?.getStateName() ?? this.preview.stateName;
    const events = this.preview.eventLog.length
      ? this.preview.eventLog.map((event) => '@' + event.atMs + 'ms f' + event.frame + ' ' + event.type + (event.name ? ':' + event.name : '')).join('\n')
      : 'no frame events emitted yet';
    this.readout.textContent = [
      'entity: ' + this.preview.entityId + (asset.sheet.isPlaceholder ? ' (placeholder)' : ''),
      'state: ' + state + (this.preview.paused ? ' (paused)' : ' (playing)'),
      'frame: ' + frame,
      'fps: ' + String(asset.meta.states[state]?.fps ?? 'n/a'),
      'events:',
      events,
    ].join('\n');
  }
}
